"use client";

import { useEffect, useRef, useState } from "react";

type VideoTrack = {
    src: string;
    kind?: "subtitles" | "captions" | "descriptions" | "chapters" | "metadata";
    srcLang?: string;
    label?: string;
    default?: boolean;
};

type VideoPlayerProps = {
    src: string;
    poster?: string;
    autoPlay?: boolean;
    loop?: boolean;
    muted?: boolean;
    controls?: boolean;
    tracks?: VideoTrack[];
    preload?: "none" | "metadata" | "auto";
    className?: string;
};

export default function VideoPlayer({
    src,
    poster,
    autoPlay = false,
    loop = false,
    muted = false,
    controls = true,
    tracks,
    preload = "none",
    className = "",
}: VideoPlayerProps) {
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const isMuted = autoPlay ? true : muted;

    const [isVisible, setIsVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.25 },
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!isVisible) return;

        const video = videoRef.current;
        if (!video) return;

        try {
            video.load();
        } catch {}
    }, [isVisible, src]);

    useEffect(() => {
        if (!isVisible) return;
        if (!autoPlay) return;

        const video = videoRef.current;
        if (!video) return;

        video.play().catch(() => {
            // Autoplay might be blocked — ignore safely
        });
    }, [isVisible, autoPlay]);

    return (
        <div
            ref={containerRef}
            className={`relative w-full aspect-video overflow-hidden rounded-xl bg-black ${className}`}
        >
            {isVisible && isLoading && !hasError && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60 text-white text-sm">
                    Loading video...
                </div>
            )}

            {hasError && (
                <div className="absolute inset-0 flex items-center justify-center bg-red-950 text-white text-sm">
                    Failed to load video
                </div>
            )}

            {isVisible && (
                <video
                    ref={videoRef}
                    src={src}
                    poster={poster}
                    controls={controls}
                    muted={isMuted}
                    loop={loop}
                    playsInline
                    preload={preload}
                    className="w-full h-full"
                    onLoadStart={() => setIsLoading(true)}
                    onLoadedMetadata={() => setIsLoading(false)}
                    onCanPlay={() => setIsLoading(false)}
                    onPlaying={() => setIsLoading(false)}
                    onWaiting={() => setIsLoading(true)}
                    onStalled={() => setIsLoading(true)}
                    onError={() => {
                        setIsLoading(false);
                        setHasError(true);
                    }}
                >
                    {tracks?.map((track, index) => (
                        <track
                            key={index}
                            src={track.src}
                            kind={track.kind ?? "subtitles"}
                            srcLang={track.srcLang}
                            label={track.label}
                            default={track.default}
                        />
                    ))}

                    <p>Your browser does not support the video tag.</p>
                </video>
            )}
        </div>
    );
}
